/* Which columns a field inside a Repeating display may show.
 *
 * 0.41.87 — Replaces the hand-maintained BINDABLE_PROPS map of 0.41.86.
 *
 * That map said which properties of which element types could be bound,
 * and it was wrong twice over: it was a third hand-kept list of facts
 * about element types, and I truncated information_card to three of its
 * thirty-five text fields because I stopped typing.
 *
 * A field that already offers a library value is, by definition, a text
 * field an admin fills. So the column button sits beside the library
 * button — every one of them, through one change rather than
 * thirty-six — and no list of types is needed at all.
 *
 * The context is empty everywhere except inside a Repeating display
 * whose rows come from an API response, so the button appears exactly
 * where a column means something.
 */

import { createContext, useContext } from 'react';
import { listText } from '../lib/readValue';

const RepeatingColumnsContext = createContext(null);

export const RepeatingColumnsProvider = RepeatingColumnsContext.Provider;

export function useRepeatingColumns() {
  return useContext(RepeatingColumnsContext) || null;
}

/* The token a column button writes into a field.
 *
 * `{column:mark_id}` deliberately mirrors `{input:name}` in a
 * connection's request template: one placeholder vocabulary across the
 * platform rather than two that look alike and behave differently.
 *
 * It is substituted per row at render time, so it works in ANY text
 * property — a card's badge, its second line, its fifteenth section
 * value — without anything needing to know that property exists.
 */
// 0.46.16 - Brackets too: `nature.goods[].name.en` (every item of a
// list inside the row) and `details[0].text` (one item).
export const COLUMN_TOKEN_RE = /\{column:([A-Za-z0-9_.»]+(?:\[\d*\][A-Za-z0-9_.»]*)*)\}/g;

export function columnToken(name) {
  return `{column:${name}}`;
}

/* Fill every `{column:…}` in a string from one row.
 *
 * A token naming a column the row does not carry becomes empty rather
 * than being left as `{column:x}`: a citizen should never see the
 * platform's own syntax, and an empty cell is the honest rendering of
 * a value the response did not include.
 */
export function fillColumnTokens(text, row, readValue) {
  if (typeof text !== 'string' || text.indexOf('{column:') === -1) return text;
  return text.replace(COLUMN_TOKEN_RE, (_, name) => {
    const v = readValue(row, name);
    return columnText(v);
  });
}

/* 0.46.16 - A column's value as text (lib/readValue listText). */
export const columnText = listText;
